import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface PipelineRow {
  readonly pipelineId: string;
  readonly pipelineCode: string;
  readonly pipelineName: string;
  readonly pipelineType: string;
  readonly status: string;
  readonly isEnabled: boolean;
  readonly lastRunAt: string | null;
}

const TYPES = ['', 'CI', 'CD', 'CONTINUOUS_DEPLOYMENT', 'BUILD', 'TEST', 'RELEASE', 'DEPLOYMENT', 'VALIDATION'];
const STATUSES = ['', 'DRAFT', 'IDLE', 'RUNNING', 'PAUSED', 'SUCCESS', 'FAILED', 'DISABLED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M32 — Pipeline Dashboard (§31 / §17-18). Register of CI/CD pipelines (T429)
 * with type, enablement and last-run outcome plus quality-gate posture. Supports
 * creating a pipeline and enabling/disabling, executing, pausing, resuming and
 * completing runs, each wired to the guarded /v1/devops/pipelines write API.
 * Filterable by pipeline type and status so platform engineers can drive the
 * pipelines feeding the deployment workflow.
 */
export function PipelineDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [pipelineType, setPipelineType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('CI');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (pipelineType) params.set('pipelineType', pipelineType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PipelineRow[] }>(`/v1/devops/pipelines${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, pipelineType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.pipeline.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(tr('m32.pipeline.nameRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/pipelines`, {
        method: 'POST',
        body: { pipelineName: newName.trim(), pipelineType: newType },
        token,
      });
      setNotice(tr('m32.pipeline.createdNotice', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: PipelineRow, path: string, label: string, body: Record<string, unknown> = {}): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/pipelines/${r.pipelineId}/${path}`, { method: 'POST', body, token });
      setNotice(tr('m32.pipeline.actNotice', { code: r.pipelineCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="pipeline-dashboard">
      <h1>{tr('m32.pipeline.title')}</h1>

      <section aria-label="create-pipeline">
        <h2>{tr('m32.pipeline.createTitle')}</h2>
        <form aria-label="pipeline-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.pipeline.fieldName')}<input aria-label="field-pipeline-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{tr('m32.pipeline.type')}
            <select aria-label="field-pipeline-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-pipeline" disabled={busy}>{busy ? tr('m32.pipeline.creating') : tr('m32.pipeline.createBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="pipeline-error">{actionError}</p>}
        {notice && <p aria-label="pipeline-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.pipeline.type')}
          <select aria-label="filter-pipeline-type" value={pipelineType} onChange={(e) => setPipelineType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.pipeline.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.pipeline.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.pipeline.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.pipeline.loading')}</p> : (
        <>
          <p aria-label="pipeline-count">{tr('m32.pipeline.count', { count: rows.length })}</p>
          <table aria-label="pipeline-table">
            <thead><tr><th>{tr('m32.pipeline.thCode')}</th><th>{tr('m32.pipeline.fieldName')}</th><th>{tr('m32.pipeline.type')}</th><th>{tr('m32.pipeline.thEnabled')}</th><th>{tr('m32.pipeline.thLastRun')}</th><th>{tr('m32.pipeline.status')}</th><th>{tr('m32.pipeline.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pipelineId} data-status={r.status}>
                  <td>{r.pipelineCode}</td><td>{r.pipelineName}</td><td>{r.pipelineType}</td>
                  <td>{r.isEnabled ? 'YES' : 'no'}</td><td>{at(r.lastRunAt)}</td><td>{r.status}</td>
                  <td>
                    <button type="button" aria-label={`enable-${r.pipelineCode}`} onClick={() => void act(r, 'enable', r.isEnabled ? tr('m32.pipeline.act.disabled') : tr('m32.pipeline.act.enabled'), { enabled: !r.isEnabled })}>{r.isEnabled ? tr('m32.pipeline.disable') : tr('m32.pipeline.enable')}</button>
                    <button type="button" aria-label={`execute-${r.pipelineCode}`} onClick={() => void act(r, 'execute', tr('m32.pipeline.act.executionStarted'))}>{tr('m32.pipeline.execute')}</button>
                    {r.status === 'RUNNING' && (
                      <>
                        <button type="button" aria-label={`pause-${r.pipelineCode}`} onClick={() => void act(r, 'pause', tr('m32.pipeline.act.paused'))}>{tr('m32.pipeline.pause')}</button>
                        <button type="button" aria-label={`complete-${r.pipelineCode}`} onClick={() => void act(r, 'complete', tr('m32.pipeline.act.completed'))}>{tr('m32.pipeline.complete')}</button>
                      </>
                    )}
                    {r.status === 'PAUSED' && (
                      <button type="button" aria-label={`resume-${r.pipelineCode}`} onClick={() => void act(r, 'resume', tr('m32.pipeline.act.resumed'))}>{tr('m32.pipeline.resume')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
